import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Syringe, Plus, Loader2, Calendar, Search, Printer, FileDown, FileText, Trash2,
  CheckCircle2, RefreshCw, IndianRupee, Users, Ticket, AlertTriangle, ArrowLeft, BarChart3, UserPlus, X, Pencil,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return s; } };

// Fetch the auth-protected receipt HTML, then open it in a new tab so window.print can fire.
// `window.open(url)` directly fails because the browser doesn't send the Bearer token on a top-level GET.
const openReceipt = async (api, year, regId) => {
  try {
    const res = await api.get(`/vaccination/${year}/registrations/${regId}/receipt?_=${Date.now()}`, { responseType: 'text', transformResponse: [(d) => d] });
    const html = res.data;
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Pop-up blocked. Please allow pop-ups to print the receipt.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Failed to open receipt');
  }
};


// =============== YEAR PICKER LANDING ===============
const YearLanding = ({ years, loading, onPick, onCreate, onDelete, onComplete, onReopen }) => (
  <div className="space-y-4">
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex-1">
        <h2 className="text-lg font-bold flex items-center gap-2 text-emerald-800">
          <Syringe className="w-5 h-5" /> Vaccination Drive
        </h2>
        <p className="text-xs text-slate-500">Anjuman e Saifee · Nagpur</p>
      </div>
      <Button onClick={onCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-vac-year">
        <Plus className="w-4 h-4 mr-1" /> New Year
      </Button>
    </div>

    {loading ? (
      <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
    ) : years.length === 0 ? (
      <Card><CardContent className="p-10 text-center text-slate-500">
        <Syringe className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <div className="text-sm">No drives configured yet. Click <strong>New Year</strong> to set the first one up.</div>
      </CardContent></Card>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {years.map((y) => (
          <Card key={y.year} className="hover:shadow-md transition-shadow border-emerald-100" data-testid={`vac-year-${y.year}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase text-slate-500 font-semibold tracking-wide">Year</div>
                  <div className="text-2xl font-extrabold text-emerald-800">{y.year}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Per Token</div>
                  <div className="text-lg font-bold text-slate-800">{fmtMoney(y.amount)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-slate-500">Tokens</div>
                  <div className="text-base font-bold">{y.tokens_count || 0}</div>
                </div>
                <div className={`rounded p-2 ${y.completed ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>
                  <div>Status</div>
                  <div className="font-semibold flex items-center gap-1">
                    {y.completed ? <CheckCircle2 className="w-3 h-3" /> : <Calendar className="w-3 h-3" />} {y.completed ? 'Completed' : 'Open'}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => onPick(y)} data-testid={`open-vac-${y.year}`}>
                  Open
                </Button>
                {y.completed ? (
                  <Button size="sm" variant="outline" onClick={() => onReopen(y)}><RefreshCw className="w-3 h-3" /></Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => onComplete(y)}><CheckCircle2 className="w-3 h-3" /></Button>
                )}
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDelete(y)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )}
  </div>
);

// =============== CREATE YEAR DIALOG ===============
const NewYearDialog = ({ open, onClose, onSaved }) => {
  const api = useApi();
  const [year, setYear] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setYear(''); setAmount(''); } }, [open]);
  const save = async () => {
    if (!year.trim()) { toast.error('Year is required'); return; }
    setBusy(true);
    try {
      await api.post('/vaccination/years', { year: year.trim(), amount: Number(amount) || 0 });
      toast.success(`Year ${year.trim()} created`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="new-year-dialog">
        <DialogHeader><DialogTitle>New Vaccination Drive Year</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Year *</Label><Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="e.g. 2026" data-testid="new-year-input" /></div>
          <div><Label>Vaccination Amount (₹) *</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 250" data-testid="new-amount-input" /></div>
          <p className="text-xs text-slate-500">This amount auto-fills every token receipt and the collection summary.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-year-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============== REGISTER DIALOG ===============
const RegisterDialog = ({ open, onClose, year, amountPerToken, onSaved }) => {
  const api = useApi();
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);     // when chosen from search
  const [manualMode, setManualMode] = useState(false);
  const [form, setForm] = useState({ name: '', its: '', mobile: '', city: '' });
  const [numVaccinations, setNumVaccinations] = useState(1);
  const [paymentStatus, setPaymentStatus] = useState('unpaid');
  const [nextHint, setNextHint] = useState({ suggested: 1, gaps: [], last: 0 });
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPicked(null); setManualMode(false); setForm({ name: '', its: '', mobile: '', city: '' });
    setSearchQ(''); setSearchResults([]); setNumVaccinations(1); setPaymentStatus('unpaid');
    setTokens([]);
    setAssignees([]); setAssigneeSearch({}); setAssigneeResults({}); setAssigneeBusy({});
    api.get(`/vaccination/${year}/next-token`).then(r => {
      const d = r.data || { suggested: 1, gaps: [], last: 0 };
      setNextHint(d);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [open, year]);

  // Debounced fuzzy search for a specific token row.
  const searchAssignee = (idx, q) => {
    setAssigneeSearch((s) => ({ ...s, [idx]: q }));
    if (assigneeTimers.current[idx]) clearTimeout(assigneeTimers.current[idx]);
    if (!q || q.trim().length < 2) {
      setAssigneeResults((r) => ({ ...r, [idx]: [] }));
      return;
    }
    assigneeTimers.current[idx] = setTimeout(async () => {
      setAssigneeBusy((b) => ({ ...b, [idx]: true }));
      try {
        const res = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(q.trim())}&limit=10`);
        const arr = res.data?.results || res.data?.members || res.data || [];
        setAssigneeResults((r) => ({ ...r, [idx]: arr }));
      } catch { setAssigneeResults((r) => ({ ...r, [idx]: [] })); }
      finally { setAssigneeBusy((b) => ({ ...b, [idx]: false })); }
    }, 250);
  };
  const pickAssignee = (idx, m) => {
    const rawMobile = (m.mobile || m.phone || '').toString();
    const cleanMobile = rawMobile.replace(/\D/g, '').replace(/^91/, '').slice(-10);
    setAssignees((prev) => {
      const cp = prev.slice();
      cp[idx] = {
        its: m.its_id || m.its || '',
        name: m.full_name || m.name || '',
        mobile: cleanMobile,
      };
      return cp;
    });
    setAssigneeSearch((s) => ({ ...s, [idx]: '' }));
    setAssigneeResults((r) => ({ ...r, [idx]: [] }));
  };
  const clearAssignee = (idx) => {
    setAssignees((prev) => {
      const cp = prev.slice();
      cp[idx] = null;
      return cp;
    });
  };

  useEffect(() => {
    if (!open || manualMode) return;
    if (timer.current) clearTimeout(timer.current);
    if (searchQ.trim().length < 2) { setSearchResults([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(searchQ.trim())}&limit=12`);
        const arr = res.data?.results || res.data?.members || res.data || [];
        setSearchResults(Array.isArray(arr) ? arr : []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 280);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [searchQ, open, manualMode]);

  // Auto-generate sequential tokens: fill gaps first, then continue from suggested
  const computeTokens = (count) => {
    const n = Math.max(1, Math.min(50, parseInt(count, 10) || 0));
    const gaps = (nextHint?.gaps || []).slice().sort((a, b) => a - b);
    const result = [];
    // First fill the smallest gaps
    while (result.length < n && gaps.length > 0) {
      result.push(gaps.shift());
    }
    // Then continue from suggested
    let next = nextHint?.suggested || 1;
    // Skip tokens already in result
    const used = new Set(result);
    while (result.length < n) {
      while (used.has(next)) next += 1;
      result.push(next);
      used.add(next);
      next += 1;
    }
    return result;
  };
  // Track the editable token list separately so admins can override individual numbers
  const [tokens, setTokens] = useState([]);
  // Per-token attendee overrides (used when 1 registration spans multiple people).
  // assignees[i] is the attendee for tokens[i]: { name, its, mobile } | null.
  const [assignees, setAssignees] = useState([]);
  // Per-row search state for picking attendees on each extra token.
  const [assigneeSearch, setAssigneeSearch] = useState({});   // { [idx]: query }
  const [assigneeResults, setAssigneeResults] = useState({}); // { [idx]: results[] }
  const [assigneeBusy, setAssigneeBusy] = useState({});       // { [idx]: bool }
  const assigneeTimers = useRef({});

  useEffect(() => {
    // Re-seed whenever count or next-token hint changes
    setTokens(computeTokens(numVaccinations));
    setAssignees((prev) => {
      const next = Array.from({ length: numVaccinations }, (_, i) => prev[i] || null);
      return next;
    });
    // eslint-disable-next-line
  }, [numVaccinations, nextHint?.suggested, nextHint?.last]);
  const updateToken = (idx, raw) => {
    const v = parseInt(raw, 10);
    setTokens((prev) => {
      const cp = prev.slice();
      cp[idx] = Number.isFinite(v) && v > 0 ? v : '';
      return cp;
    });
  };
  const cleanTokens = tokens.filter((t) => Number.isFinite(t) && t > 0);
  const hasDuplicates = new Set(cleanTokens).size !== cleanTokens.length;
  const totalAmt = cleanTokens.length * Number(amountPerToken || 0);

  const choose = (m) => {
    setPicked(m);
    // Strip +91 / leading zeros if present so the 10-digit validator passes
    const rawMobile = (m.mobile || m.phone || '').toString();
    const cleanMobile = rawMobile.replace(/\D/g, '').replace(/^91/, '').slice(-10);
    setForm({
      name: m.full_name || m.name || '',
      its: m.its_id || m.its || '',
      mobile: cleanMobile,
      city: m.city || '',
    });
    setSearchResults([]); setSearchQ('');
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!/^\d{10}$/.test((form.mobile || '').replace(/\D/g, ''))) { toast.error('Mobile must be 10 digits'); return; }
    if (cleanTokens.length === 0) { toast.error('At least one token number is required'); return; }
    if (cleanTokens.length !== tokens.length) { toast.error('All token numbers must be filled in'); return; }
    if (hasDuplicates) { toast.error('Token numbers must be unique'); return; }
    setBusy(true);
    try {
      const body = {
        year, name: form.name.trim(), its: form.its.trim(), mobile: form.mobile.replace(/\D/g, ''),
        city: form.city.trim(), tokens: cleanTokens, source: manualMode ? 'manual' : 'its',
        payment_status: paymentStatus,
        // Build per-token assignee overrides for tokens that have a picked attendee.
        assignees: tokens
          .map((t, i) => ({ t, a: assignees[i] }))
          .filter((x) => x.a && (x.a.its || x.a.name))
          .map((x) => ({ token_no: x.t, its: x.a.its || '', name: x.a.name || '', mobile: x.a.mobile || '' })),
      };
      const res = await api.post(`/vaccination/${year}/register`, body);
      toast.success(res.data?.message || 'Registered');
      // Open the first token's receipt for immediate print
      const firstId = res.data?.registrations?.[0]?.id;
      if (firstId) await openReceipt(api, year, firstId);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="register-vac-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ticket className="w-5 h-5 text-emerald-700" /> New Registration · {year}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button size="sm" variant={!manualMode ? 'default' : 'outline'} className={!manualMode ? 'bg-emerald-600 hover:bg-emerald-700' : ''} onClick={() => { setManualMode(false); setPicked(null); }} data-testid="mode-its-btn">
              <Search className="w-3 h-3 mr-1" /> Search Member
            </Button>
            <Button size="sm" variant={manualMode ? 'default' : 'outline'} className={manualMode ? 'bg-emerald-600 hover:bg-emerald-700' : ''} onClick={() => { setManualMode(true); setPicked(null); setForm({ name: '', its: '', mobile: '', city: '' }); }} data-testid="mode-manual-btn">
              <UserPlus className="w-3 h-3 mr-1" /> Manual Entry
            </Button>
          </div>

          {!manualMode && !picked && (
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
              <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search by ITS / Name" className="pl-8" data-testid="vac-search-input" />
              {searchQ.trim().length >= 2 && (
                <div className="absolute z-50 mt-1 left-0 right-0 bg-white border rounded-md shadow-lg max-h-72 overflow-y-auto">
                  {searching ? (
                    <div className="p-3 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
                  ) : searchResults.length === 0 ? (
                    <div className="p-3 text-sm text-slate-500 text-center">No matches — switch to <button className="underline text-emerald-700" onClick={() => setManualMode(true)}>Manual Entry</button>?</div>
                  ) : (searchResults.slice(0, 12).map((m, i) => (
                    <button key={m.id || m.its_id || i} type="button" onClick={() => choose(m)} className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-b-0">
                      <div className="text-sm font-medium">{m.full_name || m.name}</div>
                      <div className="text-xs text-slate-500">ITS {m.its_id || m.its || '—'} · {m.mobile || m.phone || '—'}</div>
                    </button>
                  )))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} readOnly={!manualMode && !!picked} data-testid="vac-name" /></div>
            <div><Label>ITS</Label><Input value={form.its} onChange={(e) => setForm({ ...form, its: e.target.value })} placeholder="8 digits (optional)" readOnly={!manualMode && !!picked} maxLength={8} data-testid="vac-its" /></div>
            <div><Label>Mobile *</Label><Input value={form.mobile} onChange={(e) => {
              // Auto-strip non-digits and a leading 91 country code if present
              const clean = e.target.value.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '').slice(0, 10);
              setForm({ ...form, mobile: clean });
            }} placeholder="10 digits" maxLength={10} data-testid="vac-mobile" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="vac-city" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Number of Vaccinations *</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={numVaccinations}
                onChange={(e) => setNumVaccinations(e.target.value)}
                data-testid="vac-num-vaccinations"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Last issued: <strong className="text-emerald-700">{nextHint?.last ? `#${nextHint.last}` : '—'}</strong> · Next: <strong className="text-emerald-700">#{nextHint?.suggested}</strong>
              </div>
            </div>
            <div>
              <Label>Payment Status *</Label>
              <div className="flex gap-2 mt-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant={paymentStatus === 'paid' ? 'default' : 'outline'}
                  className={paymentStatus === 'paid' ? 'bg-emerald-600 hover:bg-emerald-700 flex-1' : 'flex-1'}
                  onClick={() => setPaymentStatus('paid')}
                  data-testid="vac-pay-paid"
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={paymentStatus === 'unpaid' ? 'default' : 'outline'}
                  className={paymentStatus === 'unpaid' ? 'bg-rose-600 hover:bg-rose-700 flex-1' : 'flex-1'}
                  onClick={() => setPaymentStatus('unpaid')}
                  data-testid="vac-pay-unpaid"
                >
                  <AlertTriangle className="w-3 h-3 mr-1" /> Unpaid
                </Button>
              </div>
            </div>
          </div>

          {tokens.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
              <div className="text-emerald-900 font-medium mb-1.5 text-xs flex items-center justify-between">
                <span>Token numbers ({tokens.length}){tokens.length > 1 ? ' — edit number or attach extra attendee' : ''}:</span>
                {hasDuplicates && <span className="text-red-600 text-[11px]">Duplicates not allowed</span>}
              </div>
              {tokens.length === 1 ? (
                <div className="flex flex-wrap gap-1.5">
                  {tokens.map((t, i) => (
                    <div key={i} className="flex items-center bg-white border border-emerald-300 rounded overflow-hidden">
                      <span className="bg-emerald-600 text-white text-[11px] font-bold px-1.5 py-0.5">#</span>
                      <input
                        type="number"
                        min="1"
                        value={t === '' ? '' : t}
                        onChange={(e) => updateToken(i, e.target.value)}
                        className="w-14 px-1.5 py-0.5 text-[12px] font-bold text-emerald-800 focus:outline-none"
                        data-testid={`vac-token-input-${i}`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {tokens.map((t, i) => {
                    const a = assignees[i];
                    return (
                      <div key={i} className="bg-white border border-emerald-200 rounded p-1.5 space-y-1" data-testid={`vac-token-row-${i}`}>
                        <div className="flex items-center gap-1.5">
                          <div className="flex items-center bg-white border border-emerald-300 rounded overflow-hidden">
                            <span className="bg-emerald-600 text-white text-[11px] font-bold px-1.5 py-0.5">#</span>
                            <input
                              type="number"
                              min="1"
                              value={t === '' ? '' : t}
                              onChange={(e) => updateToken(i, e.target.value)}
                              className="w-14 px-1.5 py-0.5 text-[12px] font-bold text-emerald-800 focus:outline-none"
                              data-testid={`vac-token-input-${i}`}
                            />
                          </div>
                          {i === 0 ? (
                            <span className="text-[11px] text-slate-500 italic">Payer (main name above)</span>
                          ) : a ? (
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                              <Badge className="bg-emerald-600 text-[10px] truncate max-w-[180px]">{a.name}</Badge>
                              {a.its && <span className="text-[10px] text-slate-500 font-mono">ITS {a.its}</span>}
                              <Button type="button" size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] text-rose-600" onClick={() => clearAssignee(i)} data-testid={`vac-clear-assignee-${i}`}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex-1 relative">
                              <Search className="absolute left-2 top-1.5 w-3 h-3 text-slate-400" />
                              <Input
                                value={assigneeSearch[i] || ''}
                                onChange={(e) => searchAssignee(i, e.target.value)}
                                placeholder="Search ITS / partial / full name"
                                className="h-7 text-xs pl-7"
                                data-testid={`vac-assignee-search-${i}`}
                              />
                              {(assigneeSearch[i] || '').trim().length >= 2 && (
                                <div className="absolute z-20 top-full left-0 right-0 bg-white border border-emerald-200 rounded shadow-md mt-1 max-h-48 overflow-auto">
                                  {assigneeBusy[i] ? (
                                    <div className="p-2 text-center text-slate-500 text-xs"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Searching…</div>
                                  ) : (assigneeResults[i] || []).length === 0 ? (
                                    <div className="p-2 text-center text-slate-500 text-xs">No matches</div>
                                  ) : (assigneeResults[i] || []).map((m, j) => (
                                    <button key={m.id || m.its_id || j} type="button" onClick={() => pickAssignee(i, m)} className="w-full text-left px-2 py-1.5 hover:bg-emerald-50 border-b last:border-b-0">
                                      <div className="text-xs font-medium">{m.full_name || m.name}</div>
                                      <div className="text-[10px] text-slate-500">ITS {m.its_id || m.its || '—'} · {m.mobile || m.phone || '—'}</div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="text-right text-emerald-800 font-bold mt-1.5 text-xs">
                {cleanTokens.length} × {fmtMoney(amountPerToken)} = {fmtMoney(totalAmt)}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-vac-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Printer className="w-4 h-4 mr-1" />} Register & Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============== DASHBOARD ===============
const Dashboard = ({ stats, loading }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
      {[
        { label: 'Registrations', val: stats?.total_registrations, icon: Users, color: 'text-blue-700 bg-blue-100' },
        { label: 'Tokens', val: stats?.total_tokens, icon: Ticket, color: 'text-emerald-700 bg-emerald-100' },
        { label: 'Collection', val: fmtMoney(stats?.total_collection), icon: IndianRupee, color: 'text-amber-700 bg-amber-100' },
        { label: 'Manual', val: stats?.total_manual_entries, icon: UserPlus, color: 'text-violet-700 bg-violet-100' },
        { label: "Today Reg.", val: stats?.today_registrations, icon: Calendar, color: 'text-cyan-700 bg-cyan-100' },
        { label: "Today Tokens", val: stats?.today_tokens, icon: Ticket, color: 'text-cyan-700 bg-cyan-100' },
        { label: "Today Coll.", val: fmtMoney(stats?.today_collection), icon: IndianRupee, color: 'text-rose-700 bg-rose-100' },
      ].map((s, i) => (
        <Card key={i} className="border">
          <CardContent className="p-3 flex items-center gap-2">
            <div className={`w-9 h-9 rounded-md flex items-center justify-center ${s.color}`}><s.icon className="w-4 h-4" /></div>
            <div>
              <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">{s.label}</div>
              <div className="text-base font-extrabold text-slate-800 leading-none mt-0.5">{s.val ?? 0}</div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
    <Card>
      <CardHeader className="pb-1"><CardTitle className="text-sm flex items-center gap-1"><BarChart3 className="w-4 h-4" /> Day-wise activity</CardTitle></CardHeader>
      <CardContent>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (stats?.series || []).length === 0 ? (
          <div className="text-xs text-slate-500 text-center py-4">No activity yet.</div>
        ) : (
          <SimpleSeriesChart series={stats.series} />
        )}
      </CardContent>
    </Card>
  </div>
);

const SimpleSeriesChart = ({ series }) => {
  const maxTokens = Math.max(1, ...series.map(s => s.tokens));
  const maxColl = Math.max(1, ...series.map(s => s.collection));
  return (
    <div className="space-y-1.5">
      {series.map((s) => (
        <div key={s.date} className="text-xs">
          <div className="flex justify-between mb-0.5">
            <span className="font-medium">{s.date}</span>
            <span className="text-slate-500">{s.tokens} tokens · {fmtMoney(s.collection)}</span>
          </div>
          <div className="flex gap-1 h-3">
            <div className="bg-emerald-500 rounded-sm" style={{ width: `${(s.tokens / maxTokens) * 50}%` }} />
            <div className="bg-amber-500 rounded-sm" style={{ width: `${(s.collection / maxColl) * 50}%` }} />
          </div>
        </div>
      ))}
      <div className="flex gap-3 text-[10px] text-slate-500 mt-1">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded" /> Tokens</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded" /> Collection</span>
      </div>
    </div>
  );
};

// =============== EDIT MAIN REGISTRATION DIALOG (payer fields) ===============
const EditMainDialog = ({ open, row, onClose, onSave }) => {
  const api = useApi();
  const [form, setForm] = useState({ its: '', name: '', mobile: '', city: '' });
  const [applyToBatch, setApplyToBatch] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const t = useRef(null);

  useEffect(() => {
    if (!open || !row) return;
    setForm({
      its: row.its || '',
      name: row.name || '',
      mobile: row.mobile || '',
      city: row.city || '',
    });
    setApplyToBatch(false);
    setSearchQ(''); setResults([]);
  }, [open, row]);

  useEffect(() => {
    if (t.current) clearTimeout(t.current);
    if (!searchQ.trim() || searchQ.trim().length < 2) { setResults([]); return; }
    t.current = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(searchQ.trim())}&limit=10`);
        const arr = res.data?.results || res.data?.members || res.data || [];
        setResults(arr);
      } catch { setResults([]); }
      finally { setBusy(false); }
    }, 250);
    // eslint-disable-next-line
  }, [searchQ]);

  const pick = (m) => {
    const rawMobile = (m.mobile || m.phone || '').toString();
    const cleanMobile = rawMobile.replace(/\D/g, '').replace(/^91/, '').slice(-10);
    setForm({
      its: m.its_id || m.its || '',
      name: m.full_name || m.name || '',
      mobile: cleanMobile,
      city: form.city,
    });
    setSearchQ(''); setResults([]);
  };

  const submit = () => {
    if (form.its && !/^\d{8}$/.test(form.its.trim())) { toast.error('ITS must be 8 digits'); return; }
    if (form.mobile && !/^\d{10}$/.test((form.mobile || '').replace(/\D/g, ''))) { toast.error('Mobile must be 10 digits'); return; }
    onSave({
      its: form.its.trim(),
      name: form.name.trim(),
      mobile: (form.mobile || '').replace(/\D/g, ''),
      city: form.city.trim(),
      apply_to_batch: applyToBatch,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-main-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-emerald-700" />
            Edit Registration · Token #{row?.token_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500">
            Update the payer's ITS / name / mobile / city. Use the search below to autofill from a Jamaat member, or type directly.
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search ITS / partial / full name…"
              className="pl-8"
              data-testid="edit-main-search"
            />
            {searchQ.trim().length >= 2 && (
              <div className="absolute z-20 top-full left-0 right-0 bg-white border border-emerald-200 rounded shadow-md mt-1 max-h-48 overflow-auto">
                {busy ? (
                  <div className="p-2 text-center text-slate-500 text-xs"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Searching…</div>
                ) : results.length === 0 ? (
                  <div className="p-2 text-center text-slate-500 text-xs">No matches</div>
                ) : results.map((m, j) => (
                  <button key={m.id || m.its_id || j} type="button" onClick={() => pick(m)} className="w-full text-left px-2 py-1.5 hover:bg-emerald-50 border-b last:border-b-0">
                    <div className="text-xs font-medium">{m.full_name || m.name}</div>
                    <div className="text-[10px] text-slate-500">ITS {m.its_id || m.its || '—'} · {m.mobile || m.phone || '—'}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-600">ITS (8 digits)</label>
              <Input value={form.its} onChange={(e) => setForm({ ...form, its: e.target.value })} placeholder="ITS" className="font-mono" data-testid="edit-main-its" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Mobile</label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="10-digit" data-testid="edit-main-mobile" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-600">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Payer name" data-testid="edit-main-name" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-600">City</label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" data-testid="edit-main-city" />
            </div>
          </div>
          {row?.batch_id && (
            <label className="flex items-start gap-2 text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded p-2 cursor-pointer" data-testid="edit-main-batch-label">
              <input
                type="checkbox"
                checked={applyToBatch}
                onChange={(e) => setApplyToBatch(e.target.checked)}
                className="mt-0.5"
                data-testid="edit-main-batch"
              />
              <span>Apply to every token in this batch (this registration was issued as part of a multi-token batch).</span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="edit-main-save">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============== EDIT ATTENDEE DIALOG (for existing token receipts) ===============
const EditAttendeeDialog = ({ open, row, onClose, onSave }) => {
  const api = useApi();
  const [form, setForm] = useState({ its: '', name: '', mobile: '' });
  const [searchQ, setSearchQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const t = useRef(null);

  useEffect(() => {
    if (!open || !row) return;
    setForm({
      its: row.attendee_its || '',
      name: row.attendee_name || '',
      mobile: row.attendee_mobile || '',
    });
    setSearchQ(''); setResults([]);
  }, [open, row]);

  useEffect(() => {
    if (t.current) clearTimeout(t.current);
    if (!searchQ.trim() || searchQ.trim().length < 2) { setResults([]); return; }
    t.current = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(searchQ.trim())}&limit=10`);
        const arr = res.data?.results || res.data?.members || res.data || [];
        setResults(arr);
      } catch { setResults([]); }
      finally { setBusy(false); }
    }, 250);
    // eslint-disable-next-line
  }, [searchQ]);

  const pick = (m) => {
    const rawMobile = (m.mobile || m.phone || '').toString();
    const cleanMobile = rawMobile.replace(/\D/g, '').replace(/^91/, '').slice(-10);
    setForm({
      its: m.its_id || m.its || '',
      name: m.full_name || m.name || '',
      mobile: cleanMobile,
    });
    setSearchQ(''); setResults([]);
  };

  const submit = () => {
    if (!form.its.trim() && !form.name.trim()) { toast.error('Provide at least a name or ITS'); return; }
    if (form.its && !/^\d{8}$/.test(form.its.trim())) { toast.error('ITS must be 8 digits'); return; }
    onSave({ its: form.its.trim(), name: form.name.trim(), mobile: (form.mobile || '').replace(/\D/g, '') });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-attendee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-700" />
            Attach Attendee · Token #{row?.token_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500">
            Receipt-holder: <strong>{row?.name}</strong>{row?.its && <> · ITS <span className="font-mono">{row.its}</span></>}
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search ITS / partial / full name…"
              className="pl-8"
              data-testid="edit-attendee-search"
            />
            {searchQ.trim().length >= 2 && (
              <div className="absolute z-20 top-full left-0 right-0 bg-white border border-emerald-200 rounded shadow-md mt-1 max-h-48 overflow-auto">
                {busy ? (
                  <div className="p-2 text-center text-slate-500 text-xs"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Searching…</div>
                ) : results.length === 0 ? (
                  <div className="p-2 text-center text-slate-500 text-xs">No matches</div>
                ) : results.map((m, j) => (
                  <button key={m.id || m.its_id || j} type="button" onClick={() => pick(m)} className="w-full text-left px-2 py-1.5 hover:bg-emerald-50 border-b last:border-b-0">
                    <div className="text-xs font-medium">{m.full_name || m.name}</div>
                    <div className="text-[10px] text-slate-500">ITS {m.its_id || m.its || '—'} · {m.mobile || m.phone || '—'}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-600">ITS (8 digits)</label>
              <Input value={form.its} onChange={(e) => setForm({ ...form, its: e.target.value })} placeholder="ITS" className="font-mono" data-testid="edit-attendee-its" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Mobile</label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="10-digit" data-testid="edit-attendee-mobile" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-600">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Attendee name" data-testid="edit-attendee-name" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="edit-attendee-save">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============== REGISTRATIONS LIST ===============
const RegistrationsTab = ({ year, onChange }) => {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q.trim()) p.set('q', q.trim());
      if (source) p.set('source', source);
      const r = await api.get(`/vaccination/${year}/registrations?${p.toString()}`);
      setRows(r.data?.registrations || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [year, q, source]);
  useEffect(() => { load(); }, [load]);

  const printReceipt = (r) => {
    openReceipt(api, year, r.id);
  };
  const togglePayment = async (r) => {
    const newStatus = (r.payment_status || 'unpaid') === 'paid' ? 'unpaid' : 'paid';
    try {
      await api.patch(`/vaccination/${year}/registrations/${r.id}/payment`, { payment_status: newStatus });
      toast.success(`Marked as ${newStatus.toUpperCase()}`);
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete token #${r.token_no} for ${r.name}?`)) return;
    try {
      await api.delete(`/vaccination/${year}/registrations/${r.id}`);
      toast.success('Deleted'); load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  // Inline attendee editor — used to attach an ITS / name to an already-issued token
  // (e.g. when an HOF buys 3 tokens but didn't know the 2nd/3rd attendee at registration time).
  const [editRow, setEditRow] = useState(null);
  const openEditAttendee = (r) => setEditRow(r);
  const closeEditAttendee = () => setEditRow(null);
  const saveAttendee = async (payload) => {
    if (!editRow) return;
    try {
      await api.patch(`/vaccination/${year}/registrations/${editRow.id}/assignee`, payload);
      toast.success('Attendee updated');
      closeEditAttendee(); load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  // Main-payer editor — change the primary ITS / name / mobile / city on an existing receipt
  // (e.g. originally registered without an ITS in manual mode; admin now wants to attach it).
  const [editMainRow, setEditMainRow] = useState(null);
  const openEditMain = (r) => setEditMainRow(r);
  const closeEditMain = () => setEditMainRow(null);
  const saveMain = async (payload) => {
    if (!editMainRow) return;
    try {
      const r = await api.patch(`/vaccination/${year}/registrations/${editMainRow.id}`, payload);
      const n = r.data?.updated_count || 1;
      toast.success(payload.apply_to_batch ? `Updated ${n} token(s) in this batch` : 'Registration updated');
      closeEditMain(); load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ITS, name, mobile, city" className="pl-8" data-testid="vac-list-search" />
        </div>
        <select className="border rounded px-2 text-sm" value={source} onChange={(e) => setSource(e.target.value)} data-testid="vac-source-filter">
          <option value="">All sources</option>
          <option value="its">ITS Registered</option>
          <option value="manual">Manual Entry</option>
        </select>
      </div>
      <Card><CardContent className="p-0">
        {loading ? (<div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>) :
          rows.length === 0 ? (<div className="py-10 text-center text-slate-500 text-sm">No registrations.</div>) :
          (<div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left">Token #</th>
                  <th className="px-2 py-1.5 text-left">Name</th>
                  <th className="px-2 py-1.5 text-left">ITS</th>
                  <th className="px-2 py-1.5 text-left">Mobile</th>
                  <th className="px-2 py-1.5 text-left">City</th>
                  <th className="px-2 py-1.5 text-left">Source</th>
                  <th className="px-2 py-1.5 text-left">Payment</th>
                  <th className="px-2 py-1.5 text-right">Amount</th>
                  <th className="px-2 py-1.5 text-left">Date</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`vac-row-${r.token_no}`}>
                    <td className="px-2 py-1.5 font-bold text-emerald-700">#{r.token_no}</td>
                    <td className="px-2 py-1.5">
                      <div>{r.name}</div>
                      {(r.attendee_name || r.attendee_its) && (
                        <div className="text-[10px] text-emerald-700 flex items-center gap-1">
                          <UserPlus className="w-2.5 h-2.5" />
                          <span className="font-medium">{r.attendee_name || '—'}</span>
                          {r.attendee_its && <span className="text-slate-500 font-mono">ITS {r.attendee_its}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">{r.its || '—'}</td>
                    <td className="px-2 py-1.5">{r.mobile}</td>
                    <td className="px-2 py-1.5">{r.city || '—'}</td>
                    <td className="px-2 py-1.5">
                      {r.source === 'manual'
                        ? <Badge variant="outline" className="text-violet-700 border-violet-300">Manual</Badge>
                        : <Badge variant="outline" className="text-emerald-700 border-emerald-300">ITS</Badge>}
                    </td>
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        onClick={() => togglePayment(r)}
                        title="Click to toggle"
                        data-testid={`vac-pay-toggle-${r.token_no}`}
                        className={
                          (r.payment_status || 'unpaid') === 'paid'
                            ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200'
                            : 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-800 border border-rose-300 hover:bg-rose-200'
                        }
                      >
                        {(r.payment_status || 'unpaid') === 'paid'
                          ? (<><CheckCircle2 className="w-3 h-3" /> Paid</>)
                          : (<><AlertTriangle className="w-3 h-3" /> Unpaid</>)}
                      </button>
                    </td>
                    <td className="px-2 py-1.5 text-right">{fmtMoney(r.amount)}</td>
                    <td className="px-2 py-1.5 text-xs">{fmtDate(r.registration_date)}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="mr-1" onClick={() => printReceipt(r)} data-testid={`vac-print-${r.token_no}`}><Printer className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(r)}><Trash2 className="w-3 h-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>)}
      </CardContent></Card>
      <EditAttendeeDialog open={!!editRow} row={editRow} onClose={closeEditAttendee} onSave={saveAttendee} />
      <EditMainDialog open={!!editMainRow} row={editMainRow} onClose={closeEditMain} onSave={saveMain} />
    </div>
  );
};

// =============== REPORTS / EXPORTS ===============
const ReportsTab = ({ year }) => {
  const api = useApi();
  const dl = async (kind, fmt) => {
    try {
      const res = await api.get(`/vaccination/${year}/export?kind=${kind}&format=${fmt}`, { responseType: 'blob' });
      const ext = fmt === 'excel' ? 'xlsx' : 'pdf';
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `VaccinationDrive_${year}_${kind}.${ext}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Export failed'); }
  };
  const reports = [
    { key: 'registrations', label: 'Registration List', desc: 'One row per registered person + all their tokens.' },
    { key: 'tokens',        label: 'Token List',        desc: 'One row per token — detailed.' },
    { key: 'collection',    label: 'Collection Summary',desc: 'Daily tokens issued + amount collected.' },
    { key: 'manual',        label: 'Manual Entry List', desc: 'Only entries where ITS was not in the database.' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {reports.map(r => (
        <Card key={r.key}>
          <CardContent className="p-4 space-y-2">
            <div className="font-semibold text-emerald-800 flex items-center gap-2"><FileText className="w-4 h-4" /> {r.label}</div>
            <div className="text-xs text-slate-500">{r.desc}</div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => dl(r.key, 'excel')} data-testid={`export-${r.key}-excel`}>
                <FileDown className="w-3 h-3 mr-1" /> Excel
              </Button>
              <Button size="sm" variant="outline" onClick={() => dl(r.key, 'pdf')} data-testid={`export-${r.key}-pdf`}>
                <FileDown className="w-3 h-3 mr-1" /> PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// =============== ROOT PAGE ===============
export default function UmoorSehatVaccinationPage() {
  const api = useApi();
  const [years, setYears] = useState([]);
  const [loadingYears, setLoadingYears] = useState(true);
  const [active, setActive] = useState(null);   // selected year object
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [tab, setTab] = useState('dashboard');

  const loadYears = useCallback(async () => {
    setLoadingYears(true);
    try { const r = await api.get('/vaccination/years'); setYears(r.data?.years || []); }
    catch (e) {
      if (e?.response?.status === 403) toast.error('You do not have access to Vaccination Drive');
      else toast.error('Failed to load years');
    }
    finally { setLoadingYears(false); }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { loadYears(); }, [loadYears]);

  const loadStats = useCallback(async () => {
    if (!active) return;
    setLoadingStats(true);
    try { const r = await api.get(`/vaccination/${active.year}/stats`); setStats(r.data); }
    catch { /* noop */ }
    finally { setLoadingStats(false); }
    // eslint-disable-next-line
  }, [active]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const completeYear = async (y) => {
    if (!window.confirm(`Mark year ${y.year} as completed? You won't be able to add new tokens until you reopen it.`)) return;
    try { await api.post(`/vaccination/years/${y.year}/complete`); toast.success('Completed'); loadYears(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const reopenYear = async (y) => {
    try { await api.post(`/vaccination/years/${y.year}/reopen`); toast.success('Reopened'); loadYears(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const deleteYear = async (y) => {
    if (!window.confirm(`Delete year ${y.year}? This is only possible if there are no registrations.`)) return;
    try { await api.delete(`/vaccination/years/${y.year}`); toast.success('Deleted'); loadYears(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      {!active ? (
        <YearLanding
          years={years}
          loading={loadingYears}
          onPick={(y) => setActive(y)}
          onCreate={() => setShowCreate(true)}
          onDelete={deleteYear}
          onComplete={completeYear}
          onReopen={reopenYear}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { setActive(null); setStats(null); }} data-testid="back-to-years">
              <ArrowLeft className="w-4 h-4 mr-1" /> Years
            </Button>
            <div className="flex-1">
              <div className="text-sm text-slate-500 flex items-center gap-1.5">
                <span>Vaccination Drive</span>
                <span className="text-slate-400">›</span>
                <span className="font-semibold text-emerald-800">{active.year}</span>
                {active.completed && <Badge className="ml-1 bg-amber-100 text-amber-800 border border-amber-300">Completed</Badge>}
              </div>
              <div className="text-xs text-slate-500">Per token: <strong className="text-emerald-700">{fmtMoney(active.amount)}</strong></div>
            </div>
            <Button onClick={() => setShowRegister(true)} disabled={active.completed} className="bg-emerald-600 hover:bg-emerald-700" data-testid="register-vac-btn">
              <Ticket className="w-4 h-4 mr-1" /> New Registration
            </Button>
          </div>

          {active.completed && (
            <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              This year is marked completed. Reopen it from the Years page to make changes.
            </div>
          )}

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="dashboard" data-testid="vac-tab-dashboard"><BarChart3 className="w-4 h-4 mr-1" /> Dashboard</TabsTrigger>
              <TabsTrigger value="registrations" data-testid="vac-tab-registrations"><Ticket className="w-4 h-4 mr-1" /> Registrations</TabsTrigger>
              <TabsTrigger value="reports" data-testid="vac-tab-reports"><FileText className="w-4 h-4 mr-1" /> Reports</TabsTrigger>
            </TabsList>
            <TabsContent value="dashboard" className="mt-3"><Dashboard stats={stats} loading={loadingStats} /></TabsContent>
            <TabsContent value="registrations" className="mt-3"><RegistrationsTab year={active.year} onChange={loadStats} /></TabsContent>
            <TabsContent value="reports" className="mt-3"><ReportsTab year={active.year} /></TabsContent>
          </Tabs>
        </>
      )}

      <NewYearDialog open={showCreate} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); loadYears(); }} />
      <RegisterDialog
        open={showRegister}
        year={active?.year}
        amountPerToken={active?.amount}
        onClose={() => setShowRegister(false)}
        onSaved={() => { setShowRegister(false); loadStats(); }}
      />
    </div>
  );
}
